// Tenant session context. Until WorkOS browser auth (C1.3), a dev session
// (controller_tenant_admin + an active tenant) drives RBAC + the dev session header
// the tenant-worker resolves into an RLS tenant context. PLAN-C1.9.
import { createContext, useContext, useEffect, type ReactNode } from "react";
import type { SympllexSession } from "@sympllex/auth";
import { setApiSession } from "./api";

export const DEV_SESSION: SympllexSession = {
  user: {
    id: "00000000-0000-0000-0000-000000000010",
    workosUserId: "wos-dev-tenant",
    email: "dpo@acme.test",
    fullName: "Dev DPO",
  },
  activeTenantId: "00000000-0000-0000-0000-0000000000aa",
  availableTenantIds: ["00000000-0000-0000-0000-0000000000aa"],
  roleCodes: ["controller_tenant_admin"],
  expiresAt: 4_102_444_800,
};

// The booted session (set by main.tsx after /auth/me, or DEV_SESSION in dev). The provider + the
// router context both read it so RBAC gates + the cockpit see the real signed-in user (C1.3 F1d).
let bootedSession: SympllexSession = DEV_SESSION;
export function setBootedSession(s: SympllexSession): void { bootedSession = s; setApiSession(s); }
export function getBootedSession(): SympllexSession { return bootedSession; }

const SessionCtx = createContext<SympllexSession | null>(null);

export function SessionProvider({ children, session }: { children: ReactNode; session?: SympllexSession }) {
  const active = session ?? getBootedSession();
  useEffect(() => { setApiSession(active); }, [active]);
  return <SessionCtx.Provider value={active}>{children}</SessionCtx.Provider>;
}

export function useSession(): SympllexSession {
  const s = useContext(SessionCtx);
  if (!s) throw new Error("useSession must be used within <SessionProvider>");
  return s;
}
